import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckIcon } from '@heroicons/react/24/outline';
import SubscriptionPlan from '../../../../models/SubscriptionPlan';
import Button from '../../../../components/form-control/Button';
import { Tab } from '@headlessui/react';
import clsx from 'classnames';
import useRoute from '../../../../hooks/routesHook';
import useLayoutHook from '../../../../hooks/layoutHook';
import BackButton from '../../../../components/BackButton';
import { Drawer } from '../../../../components/elements/Drawer';
import ActionsPopOnMe from '../../../../components/ActionsPopOnMe';
import { Edit } from 'lucide-react';
import Tooltip from '../../../../components/ToolTip';

type SubscriptionPlanDetailsProps = {
    plan: SubscriptionPlan;
    onClose?: () => void;
    isOpen: boolean;
};

const SubscriptionPlanDetails = ({ plan, onClose, isOpen }: SubscriptionPlanDetailsProps) => {
    const { getDashboardBase } = useRoute();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const { isMobile, } = useLayoutHook();

    const tabs = [
        { name: 'Overview', id: 'overview' },
        { name: 'Features', id: 'features' },
        { name: 'Pricing', id: 'pricing' },
        { name: 'Usage Limits', id: 'limits' },
    ];

    const editSubscriptionPlan= () => {
        return navigate(`${getDashboardBase()}/settings/billing/subscription-plan?id=${plan.id}`);
    };

    const renderStatusBadge = (status: string) => {
        const statusMap: Record<string, { bg: string; text: string }> = {
            ACTIVE: { bg: 'bg-green-100 text-green-800', text: 'Active' },
            INACTIVE: { bg: 'bg-gray-100 text-gray-800', text: 'Inactive' },
            DRAFT: { bg: 'bg-yellow-100 text-yellow-800', text: 'Draft' },
        };

        const statusInfo = statusMap[status] || { bg: 'bg-gray-100 text-gray-800', text: status };

        return (
            <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg}`}
            >
                {statusInfo.text}
            </span>
        );
    };

    const renderFeatureList = (features: string) => {
        if (!features) return null;
        return features.split(',').map((feature, index) => (
            <li key={index} className="flex items-start">
                <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-darkBg-mainText">{feature.trim()}</span>
            </li>
        ));
    };

    const renderPricingSection = () => (
        <div className="bg-white shadow rounded-lg p-6 dark:bg-darkBg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-500">Base Price</h3>
                    <p className="text-2xl font-semibold">
                        {plan.currency} {plan.basePrice?.toLocaleString()}
                    </p>
                </div>
                <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-500">Setup Fee</h3>
                    <p className="text-2xl font-semibold">
                        {plan.currency} {plan.setupFee?.toLocaleString()}
                    </p>
                </div>
                <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-500">Final Price</h3>
                    <p className="text-2xl font-semibold text-primary-main">
                        {plan.currency} {plan.finalPrice?.toLocaleString()}
                    </p>
                </div>
            </div>
            {plan.discountPercentage > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-700">
                        <span className="font-medium">{plan.discountPercentage}% discount</span> applied to the base price.
                    </p>
                </div>
            )}
            <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-4">Billing Cycle</h3>
                <div className="bg-gray-50 p-4 rounded-lg dark:bg-transparent dark:border dark:border-darkBg-border">
                    <p className="text-gray-700 dark:text-darkBg-mainText">
                        Billed {plan.billingCycle?.toLowerCase()} • {plan.currency} {plan.finalPrice?.toLocaleString()} per {plan.billingCycle?.toLowerCase()}
                    </p>
                    {plan.hasTrial && (
                        <div className="mt-3 p-3 bg-yellow-50 rounded-md">
                            <p className="text-sm text-yellow-700">
                                <span className="font-medium">{plan.trialDays}-day free trial</span> •
                                {plan.trialRequiresCard ? 'Credit card required' : 'No credit card required'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderUsageLimits = () => (
        <div className="bg-white shadow rounded-lg p-6 dark:bg-darkBg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Policy Limits</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Motor Policies</span>
                            <span className="text-sm font-medium">
                                {plan.unlimitedPolicies ? 'Unlimited' : plan.maxMotorPolicies?.toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Non-Motor Policies</span>
                            <span className="text-sm font-medium">
                                {plan.unlimitedPolicies ? 'Unlimited' : plan.maxNonMotorPolicies?.toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Resource Limits</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Max Users</span>
                            <span className="text-sm font-medium">{plan.maxUsers?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Max Branches</span>
                            <span className="text-sm font-medium">{plan.maxBranches?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Storage</span>
                            <span className="text-sm font-medium">{plan.maxStorageGb} GB</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">API Calls</span>
                            <span className="text-sm font-medium">{plan.maxApiCalls?.toLocaleString()}/month</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <Drawer isOpen={isOpen} onClose={() => onClose?.()} position="right" size={isMobile ? 'full' : 800} height={isMobile ? 'full' : 'auto'}>
            <div className="h-screen max-h-screen   p-5 space-y-6 mt-5">
                {/* {getSubscriptionPlanActions()?.length > 0 && (
                    <div className="flex items-center justify-end">
                        <ActionsPopOnMe actions={getSubscriptionPlanActions()} popOptionClasses="" />
                    </div>
                )} */}

                <div className="border border-primary-border rounded-lg">
                    <div className="bg-white shadow rounded-lg overflow-hidden dark:bg-darkBg">
                        <div className="px-6 py-5 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <div className='flex items-center justify-center gap-3 dark:text-darkBg-mainText'>
                                        <h2 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText"> {plan.name} </h2>
                                        <Tooltip message="Edit Subscription Plan" placement='bottom'>
                                            <Edit onClick={editSubscriptionPlan} className='h-5 w-5 cursor-pointer'/>
                                        </Tooltip>
                                    </div>
                                    
                                    <p className="mt-1 text-sm text-gray-500">{plan.tagline}</p>
                                </div>
                                <div className="mt-3 sm:mt-0">
                                    <div className="flex items-center space-x-3">
                                        {renderStatusBadge(plan.planStatus)}
                                        {plan.featured && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                Featured
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                            <Tab.List className="flex border-b border-gray-200">
                                {tabs.map((tab) => (
                                    <Tab
                                        key={tab.id}
                                        className={({ selected }: { selected: boolean }) =>
                                            clsx(
                                                'px-4 py-3 text-sm font-medium focus:outline-none',
                                                selected
                                                    ? 'border-b-2 border-primary-500 text-primary-600'
                                                    : 'text-gray-500 hover:text-gray-700 dark:text-darkBg-mainText hover:border-gray-300'
                                            )
                                        }
                                    >
                                        {tab.name}
                                    </Tab>
                                ))}
                            </Tab.List>
                            <Tab.Panels className="p-6">
                                <Tab.Panel>
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-3">Description</h3>
                                            <p className="text-gray-700 dark:text-darkBg-mainText whitespace-pre-line">{plan.description}</p>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-3">Plan Details</h3>
                                                <dl className="space-y-3">
                                                    <div className="flex justify-between">
                                                        <dt className="text-sm font-medium text-gray-500">Plan Code</dt>
                                                        <dd className="text-sm text-gray-900 dark:text-darkBg-mainText">{plan.planCode}</dd>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <dt className="text-sm font-medium text-gray-500">Plan Tier</dt>
                                                        <dd className="text-sm text-gray-900 dark:text-darkBg-mainText">{plan.planTier}</dd>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <dt className="text-sm font-medium text-gray-500">Company Type</dt>
                                                        <dd className="text-sm text-gray-900 dark:text-darkBg-mainText">{plan.companyType}</dd>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <dt className="text-sm font-medium text-gray-500">Billing Cycle</dt>
                                                        <dd className="text-sm text-gray-900 dark:text-darkBg-mainText">{plan.billingCycle}</dd>
                                                    </div>
                                                </dl>
                                            </div>

                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-3">Availability</h3>
                                                <dl className="space-y-3">
                                                    <div className="flex justify-between">
                                                        <dt className="text-sm font-medium text-gray-500">Available From</dt>
                                                        <dd className="text-sm text-gray-900 dark:text-darkBg-mainText">
                                                            {plan.availableFrom ? format(new Date(plan.availableFrom), 'PPpp') : 'Immediately'}
                                                        </dd>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <dt className="text-sm font-medium text-gray-500">Available Until</dt>
                                                        <dd className="text-sm text-gray-900 dark:text-darkBg-mainText">
                                                            {plan.availableUntil ? format(new Date(plan.availableUntil), 'PPpp') : 'No end date'}
                                                        </dd>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <dt className="text-sm font-medium text-gray-500">Visibility</dt>
                                                        <dd className="text-sm text-gray-900 dark:text-darkBg-mainText">
                                                            {plan.public ? 'Public' : 'Private'}
                                                        </dd>
                                                    </div>
                                                </dl>
                                            </div>
                                        </div>

                                        {plan.terms && (
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-3">Terms & Conditions</h3>
                                                <div className="prose prose-sm max-w-none text-gray-700 dark:text-darkBg-mainText">
                                                    {plan.terms}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Tab.Panel>

                                <Tab.Panel>
                                    <div className="space-y-6">
                                        <div className="bg-white shadow rounded-lg p-6 dark:bg-darkBg">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-4">Included Features</h3>
                                            <ul className="space-y-3">
                                                {plan.hasAnalytics && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">Advanced Analytics Dashboard</span>
                                                    </li>
                                                )}
                                                {plan.hasAdvancedReports && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">Custom Report Generation</span>
                                                    </li>
                                                )}
                                                {plan.hasApiAccess && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">API Access</span>
                                                    </li>
                                                )}
                                                {plan.hasWhiteLabel && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">White Labeling</span>
                                                    </li>
                                                )}
                                                {plan.hasCustomIntegrations && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">Custom Integrations</span>
                                                    </li>
                                                )}
                                                {plan.hasAccountManager && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">Dedicated Account Manager</span>
                                                    </li>
                                                )}
                                                {plan.hasPrioritySupport && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">Priority Support</span>
                                                    </li>
                                                )}
                                                {plan.has24x7Support && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">24/7 Support</span>
                                                    </li>
                                                )}
                                                {plan.supportResponseHours > 0 && (
                                                    <li className="flex items-start">
                                                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 dark:text-darkBg-mainText">
                                                            {plan.supportResponseHours}-hour support response time
                                                        </span>
                                                    </li>
                                                )}
                                                {plan.customFeatures && renderFeatureList(plan.customFeatures)}
                                            </ul>
                                        </div>
                                    </div>
                                </Tab.Panel>

                                <Tab.Panel>
                                    {renderPricingSection()}
                                </Tab.Panel>

                                <Tab.Panel>
                                    {renderUsageLimits()}
                                </Tab.Panel>
                            </Tab.Panels>
                        </Tab.Group>
                    </div>

                    <div className="bg-white shadow rounded-lg p-6 dark:bg-darkBg dark:border dark:border-darkBg-border">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Created</h3>
                                <p className="text-sm text-gray-900 dark:text-darkBg-mainText">
                                    {format(new Date(plan.createdOn), 'PPpp')} by {plan.createdBy?.firstName ? `${plan.createdBy.firstName} ${plan.createdBy.lastName || ''}` : 'System'}
                                </p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                                <p className="text-sm text-gray-900 dark:text-darkBg-mainText">
                                    {format(new Date(plan.updatedOn), 'PPpp')} by {plan.updatedBy?.fullName}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default SubscriptionPlanDetails;